<script lang="ts">
  import { onDestroy } from 'svelte';
  import AdminSidebar, { type AdminSection } from './admin/AdminSidebar.svelte';
  import EventsHub from './admin/EventsHub.svelte';
  import EventEditor from './admin/EventEditor.svelte';
  import GuestlistView from './admin/GuestlistView.svelte';
  import PeopleDirectory from './admin/PeopleDirectory.svelte';
  import OrdersActivityView from './admin/OrdersActivityView.svelte';
  import OrdersInventoryView from './admin/OrdersInventoryView.svelte';
  import { upsertAdminEvent, type AdminEventConfig } from '../lib/convexApi';
  import type {
    AdminGuestlistResponse,
    AdminInventorySkuRecord,
    AdminPersonRecord,
    AdminOrderRecord
  } from '../lib/types';
  import { signOut } from '../lib/auth';
  import {
    liveAdminEventsStore,
    liveAdminGuestlistStore,
    liveAdminPeopleDirectoryStore,
    liveAdminOrdersStore,
    liveAdminInventoryStore,
    type LiveQueryState
  } from '../lib/convexRealtime';

  let activeSection: AdminSection = 'events';
  let manualBusy = false;
  let sidebarBusy = false;

  let selectedEventId: string | null = null;
  let selectedOrdersEventId: string | null = null;
  let ordersTab: 'activity' | 'inventory' = 'activity';

  let showEventEditor = false;
  let editorInitial: AdminEventConfig | null = null;
  let editorSaving = false;
  let editorError: string | null = null;

  const eventsStore = liveAdminEventsStore();
  let eventsState: LiveQueryState<AdminEventConfig[]> = { data: null, loading: true, error: null };
  const unsubscribeEvents = eventsStore.subscribe(state => {
    eventsState = state;
    const list = state.data ?? [];
    if (selectedEventId && !list.some(event => event.id === selectedEventId)) {
      selectedEventId = null;
    }
  });

  let events: AdminEventConfig[] = [];
  let eventsLoading = true;
  let eventsError: string | null = null;
  $: events = eventsState.data ?? [];
  $: eventsLoading = eventsState.loading;
  $: eventsError = eventsState.error;

  let guestlistStore: ReturnType<typeof liveAdminGuestlistStore> | null = null;
  let guestlistUnsubscribe: (() => void) | null = null;
  let guestlistState: LiveQueryState<AdminGuestlistResponse | null> = { data: null, loading: false, error: null };
  let guestlist: AdminGuestlistResponse | null = null;
  let guestlistLoading = false;
  let guestlistError: string | null = null;
  $: guestlist = guestlistState.data ?? null;
  $: guestlistLoading = guestlistState.loading;
  $: guestlistError = guestlistState.error;

  let peopleStore: ReturnType<typeof liveAdminPeopleDirectoryStore> | null = null;
  let peopleUnsubscribe: (() => void) | null = null;
  let peopleState: LiveQueryState<AdminPersonRecord[]> = { data: null, loading: false, error: null };
  let people: AdminPersonRecord[] = [];
  let peopleLoading = false;
  let peopleError: string | null = null;
  $: people = peopleState.data ?? [];
  $: peopleLoading = peopleState.loading;
  $: peopleError = peopleState.error;

  let ordersStore: ReturnType<typeof liveAdminOrdersStore> | null = null;
  let ordersUnsubscribe: (() => void) | null = null;
  let ordersState: LiveQueryState<AdminOrderRecord[]> = { data: null, loading: false, error: null };
  let orders: AdminOrderRecord[] = [];
  let ordersLoading = false;
  let ordersError: string | null = null;
  $: orders = ordersState.data ?? [];
  $: ordersLoading = ordersState.loading;
  $: ordersError = ordersState.error;

  $: if (!selectedOrdersEventId && events.length > 0) {
    selectedOrdersEventId = events[0].id;
  }

  let inventoryStore: ReturnType<typeof liveAdminInventoryStore> | null = null;
  let inventoryUnsubscribe: (() => void) | null = null;
  let inventoryState: LiveQueryState<AdminInventorySkuRecord[] | null> = {
    data: null,
    loading: false,
    error: null
  };
  let inventory: AdminInventorySkuRecord[] | null = null;
  let inventoryLoading = false;
  let inventoryError: string | null = null;
  $: inventory = inventoryState.data ?? null;
  $: inventoryLoading = inventoryState.loading;
  $: inventoryError = inventoryState.error;

  function ensureGuestlistStore(eventId: string) {
    if (!guestlistStore) {
      guestlistStore = liveAdminGuestlistStore(eventId);
      guestlistUnsubscribe = guestlistStore.subscribe(state => (guestlistState = state));
    } else {
      void guestlistStore.setArgs({ eventId });
    }
  }

  function ensurePeopleStore() {
    if (!peopleStore) {
      peopleStore = liveAdminPeopleDirectoryStore();
      peopleUnsubscribe = peopleStore.subscribe(state => (peopleState = state));
    }
  }

  function ensureOrdersStore() {
    if (!ordersStore) {
      ordersStore = liveAdminOrdersStore();
      ordersUnsubscribe = ordersStore.subscribe(state => (ordersState = state));
    }
  }

  function ensureInventoryStore(eventId: string) {
    if (!inventoryStore) {
      inventoryStore = liveAdminInventoryStore(eventId);
      inventoryUnsubscribe = inventoryStore.subscribe(state => (inventoryState = state));
    } else {
      void inventoryStore.setArgs({ eventId });
    }
  }

  function openCreateEvent() {
    editorInitial = null;
    editorError = null;
    showEventEditor = true;
  }

  function openEditEvent(eventId: string) {
    const existing = events.find(event => event.id === eventId) ?? null;
    editorInitial = existing;
    editorError = null;
    showEventEditor = true;
  }

  function closeEventEditor() {
    showEventEditor = false;
    editorInitial = null;
    editorError = null;
  }

  async function handleEventSave(event: CustomEvent<{ event: AdminEventConfig }>) {
    editorSaving = true;
    editorError = null;
    manualBusy = true;
    try {
      await upsertAdminEvent(event.detail.event);
      await eventsStore.refresh();
      showEventEditor = false;
    } catch (error) {
      editorError = error instanceof Error ? error.message : 'Failed to save event';
    } finally {
      editorSaving = false;
      manualBusy = false;
    }
  }

  function handleNavigate(event: CustomEvent<{ section: AdminSection }>) {
    activeSection = event.detail.section;
    if (activeSection === 'people') ensurePeopleStore();
    if (activeSection === 'orders') {
      ensureOrdersStore();
      if (!selectedOrdersEventId && selectedEventId) {
        selectedOrdersEventId = selectedEventId;
      }
      if (ordersTab === 'inventory' && selectedOrdersEventId) {
        ensureInventoryStore(selectedOrdersEventId);
      }
    }
  }

  function openGuestlist(eventId: string) {
    selectedEventId = eventId;
    ensureGuestlistStore(eventId);
    ensurePeopleStore();
    activeSection = 'guestlist';
  }

  function handlePreview(eventId: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('event', eventId);
    window.open(url.toString(), '_blank');
  }

  async function handleSignOut() {
    await signOut();
  }

  function handleGuestRefresh() {
    void guestlistStore?.refresh();
    void peopleStore?.refresh();
  }

  function handlePeopleRefresh() {
    void peopleStore?.refresh();
    void guestlistStore?.refresh();
  }

  async function handleOrdersRefresh() {
    await ordersStore?.refresh();
  }

  async function handleInventoryRefresh() {
    if (ordersTab === 'inventory' && selectedOrdersEventId) {
      if (inventoryStore) {
        await inventoryStore.refresh();
      } else {
        ensureInventoryStore(selectedOrdersEventId);
      }
    }
  }

  function handleOrdersTabChange(tab: 'activity' | 'inventory') {
    ordersTab = tab;
    if (ordersTab === 'inventory' && selectedOrdersEventId) {
      ensureInventoryStore(selectedOrdersEventId);
    }
  }

  function handleOrdersEventChange(eventId: string | null) {
    selectedOrdersEventId = eventId;
    if (ordersTab === 'inventory' && eventId) {
      ensureInventoryStore(eventId);
    }
  }

  $: sidebarBusy =
    manualBusy ||
    eventsLoading ||
    guestlistLoading ||
    peopleLoading ||
    ordersLoading ||
    (ordersTab === 'inventory' && inventoryLoading);

  onDestroy(() => {
    unsubscribeEvents();
    eventsStore.dispose();
    guestlistUnsubscribe?.();
    guestlistStore?.dispose();
    peopleUnsubscribe?.();
    peopleStore?.dispose();
    ordersUnsubscribe?.();
    ordersStore?.dispose();
    inventoryUnsubscribe?.();
    inventoryStore?.dispose();
  });
</script>

<div class="admin-shell">
  <AdminSidebar {activeSection} busy={sidebarBusy} on:navigate={handleNavigate} />

  <main class="admin-main">
    <header class="main-header">
      <div>
        <h1>Operator workspace</h1>
        <p>Manage events, guests, and identity data with confidence.</p>
      </div>
      <button type="button" class="ghost" on:click={handleSignOut}>Sign out</button>
    </header>

    {#if activeSection === 'events'}
      <section class="events-section">
        <div class="events-toolbar">
          <button type="button" on:click={openCreateEvent}>Create event</button>
        </div>
        <EventsHub
          {events}
          loading={eventsLoading}
          error={eventsError}
          selectedEventId={selectedEventId}
          on:guestlist={(event) => openGuestlist(event.detail.eventId)}
          on:edit={(event) => openEditEvent(event.detail.eventId)}
          on:preview={(event) => handlePreview(event.detail.eventId)}
        />
        {#if showEventEditor}
          <EventEditor
            initial={editorInitial}
            saving={editorSaving}
            error={editorError}
            on:save={handleEventSave}
            on:cancel={closeEventEditor}
          />
        {/if}
      </section>
    {:else if activeSection === 'guestlist'}
      <GuestlistView
        eventId={selectedEventId}
        data={guestlist}
        loading={guestlistLoading}
        error={guestlistError}
        people={people}
        on:back={() => (activeSection = 'events')}
        on:refresh={handleGuestRefresh}
      />
    {:else if activeSection === 'people'}
      <PeopleDirectory
        people={people}
        loading={peopleLoading}
        error={peopleError}
        on:refresh={handlePeopleRefresh}
      />
    {:else if activeSection === 'orders'}
      <section class="orders-section">
        <div class="orders-tabs">
          <button
            type="button"
            class:active={ordersTab === 'activity'}
            on:click={() => handleOrdersTabChange('activity')}
          >
            Activity
          </button>
          <button
            type="button"
            class:active={ordersTab === 'inventory'}
            on:click={() => handleOrdersTabChange('inventory')}
          >
            Inventory
          </button>
        </div>

        {#if ordersTab === 'activity'}
          <OrdersActivityView
            {orders}
            loading={ordersLoading}
            error={ordersError}
            events={events}
            selectedEventId={selectedOrdersEventId}
            on:refresh={handleOrdersRefresh}
            on:changeEvent={(event) => handleOrdersEventChange(event.detail.eventId)}
          />
        {:else}
          <OrdersInventoryView
            events={events}
            inventory={inventory}
            loading={inventoryLoading}
            error={inventoryError}
            selectedEventId={selectedOrdersEventId}
            on:changeEvent={(event) => handleOrdersEventChange(event.detail.eventId)}
            on:refresh={handleInventoryRefresh}
          />
        {/if}
      </section>
    {:else}
      <section class="placeholder">
        <p>Section coming soon.</p>
      </section>
    {/if}
  </main>
</div>

<style>
  .admin-shell {
    display: flex;
    min-height: 100vh;
    background: radial-gradient(120% 120% at 10% -10%, rgba(240, 169, 74, 0.18), rgba(9, 10, 16, 0.94));
    color: var(--color-text-primary);
  }

  .admin-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    padding: var(--space-6);
    overflow-y: auto;
  }

  .main-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-4);
  }

  .main-header h1 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 600;
  }

  .main-header p {
    margin: 0.35rem 0 0;
    color: var(--color-text-muted);
  }

  .main-header .ghost {
    padding: 0.6rem 1rem;
    border-radius: var(--radius-lg);
    background: rgba(255, 255, 255, 0.08);
    border: none;
    color: inherit;
    cursor: pointer;
  }

  .events-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .events-toolbar {
    display: flex;
    justify-content: flex-end;
  }

  .events-toolbar button {
    padding: 0.65rem 1.2rem;
    border-radius: var(--radius-lg);
    border: none;
    cursor: pointer;
    font-weight: 600;
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.75), rgba(178, 127, 244, 0.55));
    color: #050608;
  }

  .orders-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  .orders-tabs {
    display: flex;
    gap: var(--space-3);
  }

  .orders-tabs button {
    padding: 0.5rem 1rem;
    border-radius: var(--radius-lg);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
    cursor: pointer;
  }

  .orders-tabs button.active {
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.85), rgba(178, 127, 244, 0.55));
    color: #050608;
  }

  .placeholder {
    padding: var(--space-6);
    border-radius: var(--radius-xl);
    background: rgba(255, 255, 255, 0.04);
    color: var(--color-text-muted);
  }

  @media (max-width: 1024px) {
    .admin-shell {
      flex-direction: column;
    }

    .admin-main {
      padding: var(--space-4);
    }

    .main-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
